import { useState, useEffect } from 'react';
import { useCachedImages } from './useCachedImages';

interface ImageSource {
  id: string;
  hash: string;
  s3Url?: string; // for FilesTable
  path?: string; // for DiaryItem
  thumbnail?: string; // for DiaryItem
}

export interface ImageUrls {
  [id: string]: {
    thumbnail: string | null;
    fullSize: string | null;
    failed?: boolean;
  };
}

/**
 * useImageUrls - loads and caches thumbnails and full-size images for a list of items.
 * @param items Array of objects with id, hash, and either s3Url or path, and optional thumbnail
 * @returns imageUrls mapping id to {thumbnail, fullSize, failed}
 */
export function useImageUrls(
  items: ImageSource[]
): ImageUrls & { loadFullSize: (item: ImageSource) => Promise<string> } {
  const { get } = useCachedImages();
  const [imageUrls, setImageUrls] = useState<ImageUrls>({});

  useEffect(() => {
    // Helper function to update image URL state
    const updateImageUrls = (
      id: string,
      thumbnail: string | null,
      fullSize: string | null,
      failed: boolean
    ) => {
      setImageUrls((prev) => ({
        ...prev,
        [id]: { thumbnail, fullSize, failed },
      }));
    };

    // Helper function to try loading the full image as fallback
    const tryFallback = async (item: ImageSource): Promise<boolean> => {
      const fullPath = item.path || item.s3Url?.replace('s3://', '') || '';
      if (!fullPath) return false;

      try {
        const fallbackUrl = await get(item.hash, fullPath);
        if (fallbackUrl) {
          updateImageUrls(item.id, fallbackUrl, fallbackUrl, false);
          return true;
        }
      } catch (fallbackError) {
        // Fallback failed, will return false
      }
      return false;
    };

    const loadImages = async () => {
      const promises = items.map(async (item) => {
        if (!item.hash || !(item.s3Url || item.path)) return;
        // Skip if already failed
        if (imageUrls[item.id]?.failed) return;

        // Construct thumbnail path
        let thumbnailPath = item.thumbnail || null;
        if (!thumbnailPath && item.s3Url) {
          const originalPath = item.s3Url.replace('s3://', '');
          thumbnailPath = originalPath + '/thumbnail.jpg';
        } else if (!thumbnailPath && item.path) {
          thumbnailPath = item.path + '/thumbnail.jpg';
        }

        try {
          // Try to load thumbnail
          let thumbnailUrl: string | null = null;
          if (thumbnailPath) {
            thumbnailUrl = await get(item.hash + '_thumb', thumbnailPath);
          }

          if (thumbnailUrl) {
            // Thumbnail loaded successfully
            updateImageUrls(item.id, thumbnailUrl, null, false);
          } else {
            // Thumbnail failed, try fallback
            const fallbackSuccess = await tryFallback(item);
            if (!fallbackSuccess) {
              updateImageUrls(item.id, null, null, true);
            }
          }
        } catch (error) {
          // Thumbnail loading threw an error, try fallback
          const fallbackSuccess = await tryFallback(item);
          if (!fallbackSuccess) {
            updateImageUrls(item.id, null, null, true);
          }
        }
      });
      await Promise.all(promises);
    };

    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Function to load full size image for a given item
  const loadFullSize = async (item: ImageSource): Promise<string> => {
    if (!item.hash || !(item.s3Url || item.path))
      throw new Error('Invalid item');

    // Check if we already have the full size image loaded (from fallback)
    const currentImageUrl = imageUrls[item.id];
    if (currentImageUrl?.fullSize) {
      return currentImageUrl.fullSize;
    }

    let fullPath = item.path || item.s3Url?.replace('s3://', '') || '';
    try {
      const url = await get(item.hash, fullPath);
      if (url) {
        setImageUrls((prev) => ({
          ...prev,
          [item.id]: {
            ...prev[item.id],
            fullSize: url,
          },
        }));
        return url;
      }
      console.log('url: ', url);
      throw new Error('Failed to load full size image');
    } catch (error) {
      throw error;
    }
  };

  // Attach the loader for full size to the result (for convenience)
  (
    imageUrls as ImageUrls & {
      loadFullSize: (item: ImageSource) => Promise<string>;
    }
  ).loadFullSize = loadFullSize;

  return imageUrls as ImageUrls & {
    loadFullSize: (item: ImageSource) => Promise<string>;
  };
}
